import { OnGridClickEventType } from "@collins/react-componments/dist/types/eventHandlers";
import { QueryObserverResult } from "@tanstack/react-query";
import { ViewBooking } from "collins-js-sdk";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState
} from "react";
import { PrefillBookingType } from "../components/Pages/Diary/Sidebar/AddBooking/AddBooking";
import { ToggleView } from "../components/Pages/Diary/SubHeader/SubHeader";
import { BookingV4 } from "../types/bookings";
import {
  BookableArea,
  BookingStage,
  Floorplan,
  VenueConfig,
  VenuePage
} from "../types/venues";
import { Enquiry } from "../types/enquiries";
import { PreorderMenu } from "../types/VenueGroup";
import { floorPlanConfigStorageDefault } from "../utils/localStorage/floorPlanConfigStorage";
import {
  GridConfigDataStorageType,
  gridConfigStorageDefault
} from "../utils/localStorage/gridConfigStorage";
import {
  RunsheetConfigDataStorageType,
  runsheetConfigStorageDefault
} from "../utils/localStorage/runsheetConfigStorage";

// Utility function for shallow comparison of objects
const shallowEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;
  if (typeof obj1 !== "object" || typeof obj2 !== "object")
    return obj1 === obj2;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) return false;
  }

  return true;
};

export type SelectedTableFilterType = {
  name: string;
  id: string;
};

export type DiaryContextType = {
  venueConfig: VenueConfig | null;
  selectedVenue?: string;
  bookings?: ViewBooking[];
  fetchBookingList?: () => Promise<
    QueryObserverResult<ViewBooking[] | undefined, Error>
  >;
  bookingsFiltered?: ViewBooking[];
  venues: VenuePage[];
  date: Date;
  isInitialising: boolean;
  /** False until saved grid size and opening hours have been applied. */
  isGridLayoutReady?: boolean;
  isViewTransitioning: boolean;
  gridConfig: GridConfigType;
  selectedBooking?: ViewBooking;
  selectedBookingV4?: BookingV4;
  stages?: BookingStage[];
  gridAreaClick?: OnGridClickEventType;
  highlightArea?: string[];
  selectedView: ToggleView;
  floorPlanConfig: FloorPlanConfigType;
  runsheetConfig: RunsheetConfigType;
  selectedTable?: SelectedTableFilterType;
  lastUpdatedTable?: BookableArea;
  showSidebar: boolean;
  editingFromPreviewModal?: boolean;
  selectedFloorplan?: Floorplan;
  editBookingId?: string;
  selectedBookingId?: string;
  openedFromEnquiry?: boolean;
  openedFromSpotlight?: boolean;
  spotlightRequestedTabKey?: string;
  selectedEnquiry?: Enquiry;
  enquiriesReturnSearch?: string;
  venuePreorderMenus?: PreorderMenu[];
  isDeletePending?: boolean;
  /** Add Booking panel open in the diary sidebar. */
  showAddBooking: boolean;
  /** Prefill for the Add Booking panel (last-used type and/or party size). */
  prefillBookingType?: PrefillBookingType | null;
  /**
   * Opens the Add Booking panel (revealing the sidebar), optionally prefilling
   * the party size while keeping the last-used booking type.
   */
  openAddBooking: (numPeople?: number) => void;

  setDiaryContext: React.Dispatch<
    React.SetStateAction<Partial<DiaryContextType>>
  >;
};
export type FloorPlanConfigType = {
  displayNonTableItems: boolean;
  displayTableNames: boolean;
  zoomLevel: number;
};

export type RunsheetConfigType = RunsheetConfigDataStorageType;
export type GridConfigType = GridConfigOptionsType & GridConfigDataStorageType;

type GridConfigOptionsType = {
  displayCurrentTime: boolean;
  startMinutes: number;
  endMinutes: number;
  colMinutes: number;

  cellHeight: number;
  cellWidth: number;
  areaPanelWidth: number;

  maxRowsForArea: number;
  mode?: "desktop" | "mobile";
  bookingCardView?: "Modal" | "PopOut";
  height?: string;
  width?: string;
};

// Memoized default configurations to prevent recreation
const gridConfigDefault: GridConfigType = {
  ...gridConfigStorageDefault,
  mode: "mobile" as const,
  bookingCardView: "PopOut" as const,
  displayCurrentTime: true,
  startMinutes: 540,
  endMinutes: 1320,
  colMinutes: 15,
  cellHeight: 40,
  cellWidth: 40,
  areaPanelWidth: 128,
  maxRowsForArea: 3,
  width: "0px",
  height: "100%"
};

const floorPlanDefault: FloorPlanConfigType = {
  ...floorPlanConfigStorageDefault
};

const runsheetDefault: RunsheetConfigType = {
  ...runsheetConfigStorageDefault
};

// Create default date once to avoid recreation
const defaultDate = new Date();

const diaryContextDefault: DiaryContextType = {
  venueConfig: null,
  selectedVenue: undefined,
  bookings: undefined, // if set to [] then no bookings found text flashes up
  bookingsFiltered: undefined, // if set to [] then no bookings found text flashes up
  venues: [],
  date: defaultDate,
  isInitialising: true,
  isGridLayoutReady: false,
  isViewTransitioning: false,
  gridConfig: gridConfigDefault,
  selectedBooking: undefined,
  selectedBookingV4: undefined,
  selectedView: "DiaryTab" as const,
  floorPlanConfig: floorPlanDefault,
  runsheetConfig: runsheetDefault,
  selectedTable: undefined,
  lastUpdatedTable: undefined,
  showSidebar: true,
  editingFromPreviewModal: false,
  openedFromEnquiry: false,
  openedFromSpotlight: false,
  spotlightRequestedTabKey: undefined,
  selectedEnquiry: undefined,
  enquiriesReturnSearch: undefined,
  venuePreorderMenus: undefined,
  isDeletePending: false,
  showAddBooking: false,
  prefillBookingType: undefined,
  openAddBooking: () => {},

  setDiaryContext: () => {}
};

/** State patch applied by openAddBooking; exported so mocks reuse the logic. */
export const openAddBookingPatch = (
  prev: Partial<DiaryContextType>,
  numPeople?: number
): Partial<DiaryContextType> => ({
  showSidebar: true,
  showAddBooking: true,
  // Keep the last-used booking type and just set the party size, so the 1-5
  // shortcuts are fast for repeat same-type walk-ins. No prior type yet -> the
  // count alone is prefilled.
  ...(numPeople
    ? { prefillBookingType: { ...prev.prefillBookingType, numPeople } }
    : {})
});

export const DiaryContext =
  createContext<DiaryContextType>(diaryContextDefault);

type DiaryContextProviderType = {
  children: ReactNode;
};
export const DiaryContextProvider = ({
  children
}: DiaryContextProviderType) => {
  const [diaryContext, setDiaryContext] = useState<Partial<DiaryContextType>>(
    {}
  );

  // Wrap setDiaryContext to debug what's being set and merge properly
  const updateDiaryContext: React.Dispatch<
    React.SetStateAction<Partial<DiaryContextType>>
  > = useCallback((newContext) => {
    setDiaryContext((prevContext) => {
      // Handle function-based updates
      const contextUpdate =
        typeof newContext === "function" ? newContext(prevContext) : newContext;

      const merged = { ...prevContext, ...contextUpdate };

      // Only update if there are actual changes
      if (shallowEqual(prevContext, merged)) {
        return prevContext; // Return previous state to prevent re-render
      }

      return merged;
    });
  }, []); // Empty dependency array since setDiaryContext is stable

  const openAddBooking = useCallback(
    (numPeople?: number) =>
      updateDiaryContext((prev) => openAddBookingPatch(prev, numPeople)),
    [updateDiaryContext]
  );

  const diaryContextValue = useMemo<DiaryContextType>(
    () => ({
      ...diaryContextDefault,
      ...diaryContext,
      openAddBooking,
      setDiaryContext: updateDiaryContext
    }),
    [diaryContext, openAddBooking, updateDiaryContext]
  );

  return (
    <DiaryContext.Provider value={diaryContextValue}>
      {children}
    </DiaryContext.Provider>
  );
};

// Custom hook for consuming the diary context with better error handling
export const useDiaryContext = (): DiaryContextType => {
  const context = React.useContext(DiaryContext);
  if (!context) {
    throw new Error(
      "useDiaryContext must be used within a DiaryContextProvider"
    );
  }
  return context;
};
